import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import PackageCard from '../components/common/PackageCard';
import EmptyState from '../components/common/EmptyState';
import { PackagesShimmer } from '../components/shimmer';
import { edosApi, useGetPackagesQuery, usePrefetch } from '../store/edosApi';
import { mapApiToPackage } from '../utils/mapApiPackage';
import { recommendPackage } from '../utils/packageRecommend';
import { useCart } from '../context/CartContext';
import { useUI } from '../context/UIContext';
import { buildRoute, ROUTES } from '../constants/routes';
import styles from './Packages.module.css';

// ─── Fetch every page of packages once ──────────────────────────────────
// The recommendation engine needs to score across the FULL catalogue, not
// just the page the user happens to be browsing. Page 1 comes from the
// usual hook (which the catalog grid also subscribes to, so no double
// fetch). Once page 1 lands and we know `last_page`, we kick off the
// remaining pages in parallel via RTK Query's imperative `initiate`.
// Each result lands in the same RTK cache, so when the user later clicks
// Next on the catalog grid the page is already there — instant render.
const useAllPackages = () => {
  const dispatch = useDispatch();
  const { data: page1, isLoading: p1Loading } = useGetPackagesQuery({ page: 1 });
  const [restItems, setRestItems] = useState([]);
  const [isFetchingRest, setIsFetchingRest] = useState(false);

  useEffect(() => {
    if (!page1?.items) return;
    const lastPage = page1.pagination?.last_page || 1;
    if (lastPage <= 1) {
      setRestItems([]);
      return;
    }
    setIsFetchingRest(true);
    const requests = [];
    for (let p = 2; p <= lastPage; p += 1) {
      requests.push(
        dispatch(edosApi.endpoints.getPackages.initiate({ page: p })).unwrap(),
      );
    }
    Promise.all(requests)
      .then((pages) => {
        // Flatten all subsequent pages into one list, in order.
        setRestItems(pages.flatMap((x) => x?.items || []));
      })
      .catch(() => setRestItems([])) // on any fetch error, fall back to page 1 only
      .finally(() => setIsFetchingRest(false));
  }, [page1, dispatch]);

  // Combine page 1 with the rest. Dedupe by id — EDOS occasionally repeats
  // the same package across paginated responses (a backend quirk), and a
  // duplicate id triggers React's "two children with the same key" warning
  // when the alternatives list happens to pick both copies.
  const items = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const p of [...(page1?.items || []), ...restItems]) {
      if (!p || p.id == null || seen.has(p.id)) continue;
      seen.add(p.id);
      out.push(p);
    }
    return out;
  }, [page1, restItems]);

  return {
    items,
    isLoading: p1Loading || isFetchingRest,
    total: page1?.pagination?.total ?? items.length,
  };
};

// ─── Quiz config ─────────────────────────────────────────────────────────
// Static for now; later we'll pull from /api/quiz/questions when the admin
// CRUD lands. Shape matches what the admin endpoint will return so the
// React rendering stays unchanged.
const QUIZ_STEPS = [
  {
    id: 'reason',
    title: 'What brings you here today?',
    options: [
      { val: 'general',   icon: '📅', label: 'General checkup',     sub: 'Annual screen, no specific concern' },
      { val: 'symptoms',  icon: '🤕', label: 'Feeling symptoms',     sub: 'Something feels off' },
      { val: 'condition', icon: '🩺', label: 'Managing a condition', sub: 'Diabetes, thyroid, hypertension' },
      { val: 'fitness',   icon: '💪', label: 'Improving fitness',    sub: 'Optimise performance or weight' },
    ],
  },
  {
    id: 'detail',
    // Question text and options switch based on what the user picked in step 1.
    title: (a) =>
      a.reason === 'symptoms'  ? 'What are you experiencing?' :
      a.reason === 'condition' ? 'Which condition?' :
      a.reason === 'fitness'   ? 'Your fitness goal?' :
                                 'What kind of checkup?',
    options: (a) => ({
      symptoms: [
        { val: 'fatigue', icon: '😴', label: 'Fatigue / low energy', sub: 'Always tired' },
        { val: 'weight',  icon: '⚖️', label: 'Weight changes',        sub: 'Unexplained gain or loss' },
        { val: 'hair',    icon: '💇', label: 'Hair fall',              sub: 'Thinning or hair loss' },
        { val: 'none',    icon: '✨', label: 'Nothing specific',       sub: 'Just a routine check' },
      ],
      condition: [
        { val: 'diabetes', icon: '🩸', label: 'Diabetes / sugar', sub: 'HbA1c, kidney markers' },
        { val: 'thyroid',  icon: '🦋', label: 'Thyroid',          sub: 'Hypo or hyperthyroidism' },
        { val: 'cardiac',  icon: '❤️', label: 'Heart health',     sub: 'Cholesterol, BP' },
        { val: 'pcod',     icon: '🌸', label: 'Hormonal / PCOD',  sub: 'Periods, hormones' },
      ],
      fitness: [
        { val: 'performance', icon: '🏋', label: 'Peak performance', sub: 'Optimise training' },
        { val: 'weightloss',  icon: '🔥', label: 'Weight management', sub: 'Metabolism markers' },
        { val: 'endurance',   icon: '🏃', label: 'Stamina',           sub: 'Iron, cardio markers' },
        { val: 'recovery',    icon: '⚡', label: 'Recovery',          sub: 'Post-illness recovery' },
      ],
      general: [
        { val: 'first',    icon: '🌱', label: 'First time ever',  sub: 'Never done one' },
        { val: 'annual',   icon: '📅', label: 'Annual routine',   sub: 'I do this yearly' },
        { val: 'referred', icon: '👨‍⚕️', label: 'Doctor referred', sub: 'Doctor asked me' },
        { val: 'family',   icon: '👨‍👩‍👧', label: 'Family history',  sub: 'Hereditary risk' },
      ],
    })[a.reason] || [],
  },
  {
    id: 'lasttest',
    title: 'When was your last health check?',
    options: [
      { val: 'recent',   icon: '✅', label: 'Under 6 months',    sub: 'Recent results available' },
      { val: 'moderate', icon: '🗓', label: '6 – 12 months ago', sub: 'Getting overdue' },
      { val: 'old',      icon: '⏳', label: 'Over a year ago',   sub: 'Time for an update' },
      { val: 'never',    icon: '🆕', label: 'Never done one',    sub: 'This will be my baseline' },
    ],
  },
];

// ─── Shared "SMART RECOMMENDATION" eyebrow (pulsing dot) ─────────────────
const CardEyebrow = ({ children }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 1.25, mb: 3,
  }}>
    <Box className={styles.pulseDot} />
    <Typography sx={{
      fontFamily: '"Space Mono", monospace',
      fontSize: 11, letterSpacing: '0.18em',
      color: '#2D5C42', fontWeight: 700,
    }}>
      {children}
    </Typography>
  </Box>
);

// ─── Result card — recommended package + alternatives + "why" bullets ────
const ResultCard = ({ result, onBook, onView, onRestart }) => {
  const p = result.primary;
  if (!p) return null;
  const card = mapApiToPackage(p);
  const alts = (result.alternatives || []).slice(0, 2).map((x) => mapApiToPackage(x));

  return (
    <Box className={styles.fadeIn} sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* RECOMMENDED FOR YOU pill */}
      <Box sx={{
        alignSelf: 'flex-start',
        fontFamily: '"Space Mono", monospace',
        fontSize: 10, fontWeight: 700, letterSpacing: '0.20em',
        color: '#2D5C42',
        background: 'rgba(45,92,66,0.10)',
        px: 1.75, py: 0.875,
        borderRadius: '24px',
        mb: 2.25,
      }}>
        RECOMMENDED FOR YOU
      </Box>

      {/* Package name — Bebas Neue display */}
      <Typography sx={{
        fontFamily: '"Bebas Neue", "Impact", sans-serif',
        fontSize: 36, fontWeight: 400, color: '#0F2419',
        lineHeight: 1.0, letterSpacing: '0.01em',
        mb: 1.25,
      }}>
        {card.name}
      </Typography>

      {/* Meta */}
      <Typography sx={{
        fontFamily: '"Space Mono", monospace',
        fontSize: 11, letterSpacing: '0.10em',
        color: '#2D5C42', mb: 2.5,
      }}>
        {card.paramCount} PARAMETERS · REPORTS IN {String(card.tat || '').toUpperCase()}
      </Typography>

      {/* Why bullets — in a light "quote" block */}
      <Box sx={{
        background: '#F5F1E8',
        borderLeft: '2px solid #2D5C42',
        borderRadius: '0 10px 10px 0',
        p: '14px 16px',
        mb: 2.75,
      }}>
        {(result.why || []).slice(0, 3).map((line, i) => (
          <Box key={i} className={styles.whyRow}>
            <Box className={styles.whyTick}><CheckIcon sx={{ fontSize: 13 }} /></Box>
            <span>{line}</span>
          </Box>
        ))}
      </Box>

      {/* Price row */}
      <Box sx={{
        display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 2.75,
        fontFamily: '"Space Mono", monospace',
      }}>
        <Typography sx={{
          fontSize: 32, fontWeight: 700, color: '#0F2419',
          letterSpacing: '-0.01em',
        }}>
          ₹{card.price.toLocaleString('en-IN')}
        </Typography>
        {card.original > card.price && (
          <Typography sx={{
            fontSize: 15, color: '#9AA298', textDecoration: 'line-through',
          }}>
            ₹{card.original.toLocaleString('en-IN')}
          </Typography>
        )}
      </Box>

      <Box sx={{ flex: 1 }} />

      {/* CTAs */}
      <Box sx={{ display: 'flex', gap: 1.25 }}>
        <Button
          onClick={() => onBook(p)}
          sx={{
            flex: 1.4,
            background: '#0F2419', color: '#F5F1E8',
            borderRadius: '14px',
            px: 2.25, py: 2,
            fontFamily: '"DM Sans", sans-serif',
            fontSize: 14, fontWeight: 500, textTransform: 'none',
            justifyContent: 'space-between',
            '&:hover': { background: '#2D5C42' },
          }}
          endIcon={<Box component="span" sx={{ fontSize: 19, lineHeight: 1 }}>→</Box>}
        >
          <span>Book this checkup</span>
        </Button>
        <Button
          onClick={() => onView(p)}
          sx={{
            flex: 1,
            background: 'transparent',
            color: '#0F2419',
            border: '1px solid rgba(15,36,25,0.20)',
            borderRadius: '14px',
            px: 2.25, py: 2,
            fontFamily: '"DM Sans", sans-serif',
            fontSize: 14, fontWeight: 500, textTransform: 'none',
            '&:hover': { background: '#F7F5EE', borderColor: 'rgba(15,36,25,0.35)' },
          }}
        >
          View details
        </Button>
      </Box>

      {/* Alternatives — compact rows */}
      {alts.length > 0 && (
        <Box sx={{ mt: 2.5 }}>
          <Typography sx={{
            fontFamily: '"Space Mono", monospace',
            fontSize: 10, letterSpacing: '0.16em',
            color: '#6B7568', fontWeight: 500, mb: 1,
          }}>
            YOU MAY ALSO CONSIDER
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.875 }}>
            {alts.map((a) => (
              <Box
                key={a.id}
                onClick={() => onView(result.alternatives.find((r) => r.id === a.id))}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  p: '10px 14px',
                  background: '#F7F5EE',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  border: '1px solid rgba(15,36,25,0.08)',
                  transition: 'border-color 0.2s, transform 0.15s',
                  '&:hover': { borderColor: 'rgba(45,92,66,0.32)', transform: 'translateX(2px)' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 18, flexShrink: 0 }}>{a.icon}</Typography>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{
                      fontSize: 13, fontWeight: 600, color: '#0F2419',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {a.name}
                    </Typography>
                    <Typography sx={{
                      fontFamily: '"Space Mono", monospace',
                      fontSize: 11, color: '#6B7568',
                    }}>
                      ₹{a.price.toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                </Box>
                <ArrowForwardIcon sx={{ fontSize: 14, color: '#6B7568', flexShrink: 0 }} />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Retake footer */}
      <Box sx={{
        mt: 2.25, pt: 1.75,
        borderTop: '1px solid rgba(15,36,25,0.08)',
        textAlign: 'center',
      }}>
        <Button
          onClick={onRestart}
          sx={{
            fontFamily: '"DM Sans", sans-serif',
            fontSize: 12, color: '#2D5C42',
            textTransform: 'none', fontWeight: 500,
            '&:hover': { bgcolor: 'transparent', color: '#F0B429' },
          }}
        >
          ↻ Retake the quiz
        </Button>
      </Box>
    </Box>
  );
};

// ─── Idle card — shown before the user runs the quiz ─────────────────────
const IdleCard = ({ onStart }) => {
  return (
    <Box className={styles.fadeIn} sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <CardEyebrow>SMART RECOMMENDATION</CardEyebrow>

      <Typography sx={{
        fontFamily: '"Bebas Neue", "Impact", sans-serif',
        fontSize: 44, fontWeight: 400,
        color: '#0F2419',
        lineHeight: 0.98, letterSpacing: '0.01em',
        mb: 2,
      }}>
        Find your right checkup.
      </Typography>

      <Typography sx={{
        fontFamily: '"DM Sans", sans-serif',
        fontSize: 15, lineHeight: 1.65, color: '#5A6560',
        mb: 4,
      }}>
        Three quick questions about your age, concerns, and family history.
        We'll match you to the package that actually fits — no upsells, no guesswork.
      </Typography>

      <Box sx={{ flex: 1 }} />

      <Button
        onClick={onStart}
        sx={{
          background: '#0F2419', color: '#F5F1E8',
          border: 'none',
          borderRadius: '14px',
          px: 2.75, py: 2.25,
          fontFamily: '"DM Sans", sans-serif',
          fontSize: 15, fontWeight: 500, textTransform: 'none',
          justifyContent: 'space-between',
          width: '100%',
          '&:hover': { background: '#2D5C42', transform: 'translateY(-1px)' },
          '&:active': { transform: 'translateY(0)' },
        }}
        endIcon={<Box component="span" sx={{ fontSize: 19, lineHeight: 1 }}>→</Box>}
      >
        <span>Get my recommendation</span>
      </Button>

      {/* Microcopy */}
      <Box sx={{
        display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap',
        mt: 2.25,
        fontFamily: '"Space Mono", monospace',
        fontSize: 10, letterSpacing: '0.14em',
        color: '#8A9089',
      }}>
        {['30 SECONDS', 'NO SIGNUP', 'NABL ACCREDITED'].map((t) => (
          <Box key={t} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box component="span" sx={{ color: '#2D5C42' }}>●</Box>
            <span>{t}</span>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ─── Quiz card — the 3-step form ─────────────────────────────────────────
const QuizCard = ({ step, answers, onPick, onBack }) => {
  const def = QUIZ_STEPS[step];
  const title = typeof def.title === 'function' ? def.title(answers) : def.title;
  const opts = typeof def.options === 'function' ? def.options(answers) : def.options;

  return (
    <Box className={styles.fadeIn} sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* Slim progress dots */}
      <Box className={styles.progress}>
        {QUIZ_STEPS.map((_, i) => (
          <Box
            key={i}
            className={[
              styles.dot,
              i === step ? styles.dotActive : '',
              i < step ? styles.dotDone : '',
            ].filter(Boolean).join(' ')}
          />
        ))}
      </Box>

      {/* QUESTION X OF Y label */}
      <Typography sx={{
        fontFamily: '"Space Mono", monospace',
        fontSize: 10, letterSpacing: '0.18em',
        color: '#2D5C42', mb: 1.5,
      }}>
        QUESTION {step + 1} OF {QUIZ_STEPS.length}
      </Typography>

      {/* Big display-font question */}
      <Typography sx={{
        fontFamily: '"Bebas Neue", "Impact", sans-serif',
        fontSize: 32, fontWeight: 400,
        color: '#0F2419',
        lineHeight: 1.05, letterSpacing: '0.01em',
        mb: 3.25,
      }}>
        {title}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {opts.map((o) => (
          <button
            key={o.val}
            type="button"
            className={styles.option}
            onClick={() => onPick(def.id, o.val)}
          >
            <Typography component="span" sx={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }}>
              {o.icon}
            </Typography>
            <Box sx={{ textAlign: 'left', minWidth: 0 }}>
              <Typography sx={{
                fontFamily: '"DM Sans", sans-serif',
                fontSize: 14, fontWeight: 500, color: '#0F2419',
              }}>
                {o.label}
              </Typography>
              <Typography sx={{
                fontSize: 11.5, color: '#6B7568', fontWeight: 400,
              }}>
                {o.sub}
              </Typography>
            </Box>
          </button>
        ))}
      </Box>

      <Box sx={{ flex: 1 }} />

      {step > 0 && (
        <Button
          onClick={onBack}
          sx={{
            mt: 2.25, justifyContent: 'flex-start',
            fontFamily: '"DM Sans", sans-serif',
            fontSize: 13, color: '#2D5C42',
            textTransform: 'none', fontWeight: 500,
            '&:hover': { bgcolor: 'transparent', color: '#F0B429' },
          }}
        >
          ← Back
        </Button>
      )}
    </Box>
  );
};

// ─── Main page ───────────────────────────────────────────────────────────
const Packages = () => {
  const navigate = useNavigate();
  const { addItem, inCart } = useCart();
  const { showToast } = useUI();

  // Quiz state — 'idle' | 'quiz' | 'result'
  const [mode, setMode] = useState('idle');
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});

  // Catalog + pagination state (only matters once user clicks "Browse all")
  const [showCatalog, setShowCatalog] = useState(false);
  const [page, setPage] = useState(1);
  const catalogRef = useRef(null);

  // Catalog query — ONE page at a time, driven by the user's pagination.
  const { data, isLoading, isFetching, isError, error } =
    useGetPackagesQuery({ page });
  const pagination = data?.pagination ?? null;
  const cards = useMemo(
    () => (data?.items ?? []).map((p, i) => mapApiToPackage(p, i)),
    [data]
  );

  // Full-catalogue query — every page, fetched once. Used ONLY for the
  // recommendation engine + idle "featured" pick so scoring covers all
  // packages, not just whatever page the catalog is on.
  const { items: allPackages, total: totalPackagesCount } = useAllPackages();

  // Pre-fetch the user's next catalog page too. Redundant once
  // useAllPackages finishes (same cache key), but covers the brief window
  // between page-1 landing and the rest finishing.
  const prefetchPackages = usePrefetch('getPackages');
  useEffect(() => {
    if (!pagination) return;
    if (pagination.current_page < pagination.last_page) {
      prefetchPackages({ page: page + 1 });
    }
  }, [pagination, page, prefetchPackages]);

  // Recommendation now scores against the WHOLE catalogue — not just the
  // current catalog page. Until allPackages finishes loading, we fall
  // back to whatever the catalog has so the result card never shows a
  // null primary mid-fetch. Wrapped in useMemo so its identity is stable
  // — otherwise downstream memos would re-run every render.
  const scoringPool = useMemo(
    () => (allPackages.length ? allPackages : (data?.items || [])),
    [allPackages, data],
  );
  const recommendation = useMemo(() => {
    if (mode !== 'result') return null;
    return recommendPackage(answers, scoringPool);
  }, [mode, answers, scoringPool]);

  // ─ Quiz handlers
  const startQuiz = useCallback(() => {
    setAnswers({});
    setStep(0);
    setMode('quiz');
  }, []);

  const restart = useCallback(() => {
    setAnswers({});
    setStep(0);
    setMode('idle');
  }, []);

  const onPick = useCallback((field, val) => {
    setAnswers((prev) => {
      const next = { ...prev, [field]: val };
      if (step < QUIZ_STEPS.length - 1) {
        setStep(step + 1);
      } else {
        // Last step → compute recommendation against the latest answers.
        setMode('result');
      }
      // We snapshot answers + step transition together so the recommendation
      // memo recomputes with the FULL answer set, including this final pick.
      return next;
    });
  }, [step]);

  const onBack = useCallback(() => {
    if (step > 0) setStep(step - 1);
  }, [step]);

  // ─ Card actions
  const goToDetail = useCallback((pkg) => {
    navigate(buildRoute(ROUTES.PACKAGE_DETAIL, { id: pkg.id }));
  }, [navigate]);

  // Add the recommended package to the cart, then route to /cart.
  // NOTE: backend Cart schema enforces item_type enum ['test', 'pkg'], so
  // packages MUST use type 'pkg' (not 'package') or the server rejects the add.
  const bookNow = useCallback((pkg) => {
    const card = mapApiToPackage(pkg);
    const item = {
      id:        String(card.id),
      type:      'pkg',
      name:      card.name,
      price:     Number(card.price) || 0,
      original:  Number(card.original) || null,
      icon:      card.icon || '🧪',
      tat:       card.tat || '',
      paramCount: card.paramCount || 0,
    };
    try {
      if (!inCart(item.id, 'pkg')) {
        addItem(item);
        showToast(`${card.name} added to cart`, 'success');
      }
      navigate('/cart');
    } catch {
      showToast('Could not add to cart', 'error');
    }
  }, [addItem, inCart, showToast, navigate]);

  // ─ Browse-all reveal — smooth-scroll into view
  const onBrowseAll = useCallback(() => {
    setShowCatalog(true);
    // Wait one frame so the catalog node is in the DOM, then scroll.
    requestAnimationFrame(() => {
      catalogRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  return (
    <Box>
      {/* ═══════════════════════════════════════════════════════════════
          HERO — 2-col grid:
            • Left: NABL eyebrow → Bebas Neue headline → DM Sans subhead → trust strip
            • Right: smart-recommendation card (3 states) → OR divider → skip card
         ═══════════════════════════════════════════════════════════════ */}
      <Box className={styles.heroShell} sx={{
        py: { xs: 7, md: 11 },
        px: { xs: 2.5, md: 6 },
      }}>
        <Container maxWidth="xl" sx={{ maxWidth: 1280, position: 'relative', zIndex: 1 }}>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' },
            gap: { xs: 6, md: 9 },
            alignItems: 'center',
          }}>
            {/* ── LEFT COLUMN ── */}
            <Box>
              {/* NABL eyebrow */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, mb: 4.5 }}>
                <Box sx={{ width: 36, height: '1px', bgcolor: '#8FCFA8' }} />
                <Typography sx={{
                  fontFamily: '"Space Mono", monospace',
                  fontSize: 11, letterSpacing: '0.18em',
                  color: '#8FCFA8',
                }}>
                  NABL ACCREDITED
                </Typography>
              </Box>

              {/* Bebas Neue display headline */}
              <Typography component="h1" sx={{
                fontFamily: '"Bebas Neue", "Impact", sans-serif',
                fontSize: { xs: 'clamp(44px, 13vw, 64px)', md: 'clamp(56px, 7.5vw, 96px)' },
                fontWeight: 400, color: '#F5F1E8',
                lineHeight: 0.92, letterSpacing: '0.005em',
                m: 0, mb: 4,
              }}>
                Stop guessing.<br />
                Start with the{' '}
                <Box component="span" sx={{ display: 'block', color: '#8FCFA8' }}>
                  right health check.
                </Box>
              </Typography>

              {/* Subhead */}
              <Typography sx={{
                fontFamily: '"DM Sans", sans-serif',
                fontSize: 17, color: '#B8C2BC', fontWeight: 400,
                lineHeight: 1.7, maxWidth: 480, mb: 5.5,
              }}>
                Answer a few simple questions and get a smart recommendation
                based on your needs, symptoms, and health goals.
              </Typography>

              {/* Trust strip */}
              <Box sx={{
                display: 'flex', flexWrap: 'wrap', gap: 3.5,
                pt: 3.5, borderTop: '1px solid rgba(255,255,255,0.08)',
              }}>
                {[
                  '14 branches in Pan India',
                  'NABL ISO 15189:2022',
                  'Reports in 24 hours',
                ].map((txt) => (
                  <Box key={txt} sx={{
                    fontFamily: '"Space Mono", monospace',
                    fontSize: 11, letterSpacing: '0.12em',
                    color: '#6B7568',
                    display: 'flex', alignItems: 'center', gap: 0.5,
                  }}>
                    <Box component="span" sx={{ color: '#4ADE80' }}>●</Box>
                    {txt}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* ── RIGHT COLUMN — Smart card + Skip card ── */}
            <Box sx={{
              display: 'flex', flexDirection: 'column',
              alignItems: { xs: 'stretch', md: 'flex-end' },
              gap: 2.25,
              width: '100%',
            }}>
              {/* Smart recommendation card (WHITE, light theme) */}
              <Box sx={{
                background: '#FFFFFF',
                border: '1px solid rgba(15,36,25,0.06)',
                borderRadius: '20px',
                p: { xs: '28px 22px', md: '36px 32px' },
                width: '100%',
                maxWidth: { md: 460 },
                minHeight: { xs: 540, md: 580 },
                display: 'flex', flexDirection: 'column',
                boxShadow: '0 30px 80px rgba(0,0,0,0.30), 0 0 0 1px rgba(15,36,25,0.02) inset',
              }}>
                {isLoading && (
                  <Box sx={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <CircularProgress size={28} sx={{ color: '#2D5C42' }} />
                  </Box>
                )}

                {!isLoading && mode === 'idle' && (
                  <IdleCard onStart={startQuiz} />
                )}

                {!isLoading && mode === 'quiz' && (
                  <QuizCard
                    step={step}
                    answers={answers}
                    onPick={onPick}
                    onBack={onBack}
                  />
                )}

                {!isLoading && mode === 'result' && recommendation && (
                  <ResultCard
                    result={recommendation}
                    onBook={bookNow}
                    onView={goToDetail}
                    onRestart={restart}
                  />
                )}
              </Box>

              {/* OR divider */}
              <Box sx={{
                width: '100%', maxWidth: { md: 460 },
                display: 'flex', alignItems: 'center', gap: 1.75,
                my: '-4px',
              }}>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.10)' }} />
                <Typography sx={{
                  fontFamily: '"Space Mono", monospace',
                  fontSize: 11, letterSpacing: '0.22em',
                  color: '#6B7568', fontWeight: 700,
                }}>
                  OR
                </Typography>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.10)' }} />
              </Box>

              {/* Skip-the-quiz card (DARK, yellow accent) */}
              <Box
                onClick={onBrowseAll}
                sx={{
                  width: '100%', maxWidth: { md: 460 },
                  background: '#122E1F',
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: '20px',
                  p: '20px 24px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 2,
                  cursor: 'pointer',
                  color: '#F5F1E8',
                  boxShadow: '0 16px 40px rgba(0,0,0,0.30), 0 0 0 1px rgba(255,255,255,0.02) inset',
                  transition: 'background 0.25s, border-color 0.25s, transform 0.15s',
                  '&:hover': {
                    background: '#163524',
                    borderColor: 'rgba(240,180,41,0.32)',
                    transform: 'translateY(-1px)',
                    '& .skipArrow': { transform: 'translateX(4px)' },
                  },
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
                  <Typography sx={{
                    fontFamily: '"Space Mono", monospace',
                    fontSize: 10, letterSpacing: '0.18em',
                    color: '#6B7568', textTransform: 'uppercase',
                  }}>
                    Skip the quiz
                  </Typography>
                  <Typography sx={{
                    fontFamily: '"DM Sans", sans-serif',
                    fontSize: 15, fontWeight: 500,
                    color: '#F0B429', lineHeight: 1.3,
                  }}>
                    Browse all {totalPackagesCount > 0 ? totalPackagesCount : ''} health checks
                  </Typography>
                </Box>
                <Box
                  className="skipArrow"
                  sx={{
                    fontSize: 22, lineHeight: 1, color: '#F0B429',
                    flexShrink: 0,
                    transition: 'transform 0.25s ease',
                  }}
                >
                  →
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════
          CATALOG — hidden until user clicks "Browse all"
         ═══════════════════════════════════════════════════════════════ */}
      {showCatalog && (
        <Box ref={catalogRef} sx={{ bgcolor: 'var(--cream)', py: { xs: 6, md: 8 } }}>
          <Container maxWidth="xl" sx={{ maxWidth: 1300, px: { xs: 2, md: 4 } }}>
            <Box sx={{
              display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: 2, mb: 5,
            }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.75 }}>
                  <Box sx={{ width: 24, height: '1.5px', bgcolor: 'var(--sage)' }} />
                  <Typography sx={{
                    fontSize: 11, fontWeight: 700, letterSpacing: '0.22em',
                    color: 'var(--sage)', textTransform: 'uppercase',
                  }}>
                    All Health Checks
                  </Typography>
                </Box>
                <Typography className="serif" sx={{
                  fontSize: { xs: 26, md: 32 },
                  fontWeight: 600, color: 'var(--ink)',
                }}>
                  Our complete package catalogue
                </Typography>
              </Box>

              <Button
                onClick={() => setShowCatalog(false)}
                sx={{
                  fontSize: 13, fontWeight: 600, color: 'var(--stone)',
                  border: '1px solid var(--border)', borderRadius: 100,
                  px: 2.5, py: 1.125, textTransform: 'none',
                  '&:hover': { bgcolor: '#fff' },
                }}
              >
                ↑ Collapse
              </Button>
            </Box>

            {/* Status row — count + updating indicator */}
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: 1, mb: 3,
            }}>
              <Typography sx={{ fontWeight: 600, color: 'var(--stone)', fontSize: 14 }}>
                {isLoading
                  ? 'Loading packages…'
                  : pagination
                    ? `${pagination.total.toLocaleString()} package${pagination.total !== 1 ? 's' : ''} available`
                    : `${cards.length} package${cards.length !== 1 ? 's' : ''}`}
              </Typography>
              {isFetching && !isLoading && (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, color: 'var(--stone)' }}>
                  <CircularProgress size={14} thickness={5} sx={{ color: 'var(--sage)' }} />
                  <Typography sx={{ fontSize: 12 }}>Updating…</Typography>
                </Box>
              )}
            </Box>

            {isLoading && <PackagesShimmer count={12} />}

            {!isLoading && isError && (
              <EmptyState
                icon="⚠"
                title="Couldn't load packages"
                description={error?.data?.message || error?.error || 'Please check your connection and try again.'}
              />
            )}

            {!isLoading && !isError && cards.length === 0 && (
              <EmptyState icon="🔍" title="No packages available" />
            )}

            {!isLoading && !isError && cards.length > 0 && (
              <>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(4, 1fr)',
                  },
                  gap: { xs: 2, md: 2.5 },
                  width: '100%',
                  opacity: isFetching ? 0.6 : 1,
                  transition: 'opacity 0.15s',
                }}>
                  {cards.map((p) => <PackageCard key={p.id} pkg={p} hideBadge hideDiscount />)}
                </Box>

                {pagination && pagination.last_page > 1 && (
                  <Box sx={{
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    gap: 2, mt: 5,
                  }}>
                    <Button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={pagination.current_page <= 1 || isFetching}
                      startIcon={<ChevronLeftIcon />}
                      sx={{
                        bgcolor: '#fff',
                        border: '1px solid var(--border-l)',
                        color: 'var(--ink)',
                        borderRadius: 100, px: 2.5, py: 1,
                        textTransform: 'none', fontWeight: 600, fontSize: 13,
                        '&:hover': { bgcolor: 'var(--parchment)' },
                        '&.Mui-disabled': { opacity: 0.4 },
                      }}
                    >
                      Prev
                    </Button>
                    <Typography sx={{ fontWeight: 700, color: 'var(--ink)', fontSize: 13 }}>
                      Page {pagination.current_page} of {pagination.last_page}
                    </Typography>
                    <Button
                      onClick={() => setPage((p) => Math.min(pagination.last_page, p + 1))}
                      disabled={pagination.current_page >= pagination.last_page || isFetching}
                      endIcon={<ChevronRightIcon />}
                      sx={{
                        bgcolor: 'var(--forest)', color: '#fff',
                        borderRadius: 100, px: 2.5, py: 1,
                        textTransform: 'none', fontWeight: 700, fontSize: 13,
                        '&:hover': { bgcolor: 'var(--ink)' },
                        '&.Mui-disabled': { opacity: 0.4 },
                      }}
                    >
                      Next
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Container>
        </Box>
      )}
    </Box>
  );
};

export default Packages;
